/* eslint-disable react-refresh/only-export-components */
import { useCallback, type KeyboardEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import clsx from 'clsx'
import type { LucideIcon } from 'lucide-react'

export interface DashboardTab {
  id: string
  label: string
  icon?: LucideIcon
}

/**
 * The active sub-page, backed by the URL `?view=` param.
 *
 * Kept in the URL rather than component state so a refresh, a deep link, or the
 * browser back button all land on the same tab — an operator who bookmarks the
 * Compliance view of the Legal board should get the Compliance view. An unknown
 * or missing param falls back to the first tab rather than rendering nothing.
 */
export function useDashboardView(
  tabs: DashboardTab[],
  paramKey = 'view',
): [string, (id: string) => void] {
  const [params, setParams] = useSearchParams()
  const ids = tabs.map((t) => t.id)
  const raw = params.get(paramKey)
  const active = raw && ids.includes(raw) ? raw : tabs[0]?.id ?? ''

  const setActive = useCallback(
    (id: string) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set(paramKey, id)
          return next
        },
        { replace: true },
      )
    },
    [paramKey, setParams],
  )

  return [active, setActive]
}

interface DashboardTabsProps {
  tabs: DashboardTab[]
  active: string
  onChange: (id: string) => void
  /** Right-aligned content on the tab row — a scope note, a count, an action. */
  trailing?: React.ReactNode
  className?: string
}

/**
 * Page-level sub-navigation for a board.
 *
 * Underline tabs, not the pill/chip tabs used *inside* panels — the different
 * treatment is deliberate information hierarchy: pills switch a view within one
 * panel, these switch the whole page. Arrow keys move between tabs (roving
 * focus) so the strip is operable without a mouse.
 */
export default function DashboardTabs({
  tabs,
  active,
  onChange,
  trailing,
  className,
}: DashboardTabsProps) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const idx = tabs.findIndex((t) => t.id === active)
    if (idx < 0) return
    let next = idx
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (idx + 1) % tabs.length
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
      next = (idx - 1 + tabs.length) % tabs.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = tabs.length - 1
    else return
    event.preventDefault()
    onChange(tabs[next].id)
  }

  return (
    <div
      className={clsx(
        'flex flex-shrink-0 items-center gap-1 border-b border-amber-400/10',
        className,
      )}
    >
      <div role="tablist" aria-label="Dashboard views" onKeyDown={onKeyDown} className="flex gap-1">
        {tabs.map((tab) => {
          const isActive = tab.id === active
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              role="tab"
              type="button"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onChange(tab.id)}
              className={clsx(
                'flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-[12px] font-medium transition-colors -mb-px',
                isActive
                  ? 'border-amber-400 text-amber-200'
                  : 'border-transparent text-stone-500 hover:text-stone-300',
              )}
            >
              {Icon && <Icon size={13} className="flex-shrink-0" />}
              {tab.label}
            </button>
          )
        })}
      </div>
      {trailing && <div className="ml-auto flex items-center gap-1.5 pb-1">{trailing}</div>}
    </div>
  )
}
